import { Input } from '@/components/ui/input';
import { FC, useMemo } from 'react';
import { Counter } from 'src/components/Counter';
import { useWeightInput } from 'src/hooks/useWeightInput';
import { Order, Cart, trpc, OrderContainer } from 'src/trpc';
import { MoneyFromCents, safeMoneyFromCents } from '@libs/services/money';
import { NumericFormat } from 'react-number-format';

interface BundlePricingState {
  weight: number;
  additionalMaterials: { [materialId: string]: number };
  usageByContainer: { [containerId: string]: number };
}

interface Props {
  order: Order;
  containers: OrderContainer[];
  cart: Cart;
  state: BundlePricingState;
  updateState: (updates: Partial<BundlePricingState>) => void;
}

export const BundlePricingForm: FC<Props> = ({ order, containers, cart, state, updateState }) => {
  const { weightInput, setWeightInput, commitWeight } = useWeightInput(state.weight);

  const { data: materials } = trpc.admin.material.getMaterials.useQuery(undefined, {
    staleTime: Infinity,
    gcTime: Infinity,
    suspense: false,
    enabled: true,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  // Extract bundle pricing details from quotedPricingDetails
  const bundleDetails = useMemo(() => {
    const containerPricing = order.quotedPricingDetails?.quote?.pricing?.containers?.[0];
    const periodTier = containerPricing?.periodTier;
    const materialPricing = order.quotedPricingDetails?.quote?.pricing?.materials;

    return {
      includedTonnage: periodTier?.includedTonnage || 0,
      includedDays: periodTier?.selectedPeriodDays || 0,
      perTonRateInCents:
        periodTier?.additionalTonRateInCents || materialPricing?.perTonRateInCents || 0,
      dailyRateInCents:
        periodTier?.additionalDayRateInCents || containerPricing?.adjustedPriceInCents || 0,
    };
  }, [order.quotedPricingDetails]);

  // Calculate additional tonnage and days
  const additionalTonnage = Math.max(0, state.weight - bundleDetails.includedTonnage);
  const additionalTonnageCost = additionalTonnage * bundleDetails.perTonRateInCents;

  // Calculate container overage for each container
  const containerOverages = useMemo(() => {
    // Extract extra days paid at checkout from cart
    const totalDaysPaidAtCheckout = cart.lineItems?.containers?.[0]?.totalDays || 0;
    const extraDaysPaidAtCheckout = Math.max(
      0,
      totalDaysPaidAtCheckout - bundleDetails.includedDays,
    );

    return containers.map((container) => {
      const usedDays = state.usageByContainer[container._id] || 0;

      // Calculate days beyond included
      const daysOver = Math.max(0, usedDays - bundleDetails.includedDays);

      // Calculate refund or additional charge (mutually exclusive)
      const refundDays = Math.max(0, extraDaysPaidAtCheckout - daysOver);
      const additionalDays = Math.max(0, daysOver - extraDaysPaidAtCheckout);

      const refundAmount = refundDays * bundleDetails.dailyRateInCents;
      const additionalCost = additionalDays * bundleDetails.dailyRateInCents;

      return {
        containerId: container._id,
        usedDays,
        additionalDays,
        additionalCost,
        refundDays,
        refundAmount,
      };
    });
  }, [
    containers,
    state.usageByContainer,
    bundleDetails.includedDays,
    bundleDetails.dailyRateInCents,
    cart.lineItems?.containers,
  ]);

  return (
    <div className="flex flex-col gap-6 w-full">
      {/* Bundle Weight Section */}
      <div className="flex flex-col gap-2 w-full">
        <label className="text-sm text-muted">
          Total Weight Used (tons) - {bundleDetails.includedTonnage} tons included
        </label>
        <div className="flex flex-row gap-2 text-sm text-muted">
          <NumericFormat
            step={0.25}
            thousandSeparator={true}
            type="text"
            inputMode="decimal"
            className="w-full"
            value={weightInput}
            min={0}
            onValueChange={(values) => setWeightInput(values.value)}
            onBlur={() => commitWeight(updateState)}
            customInput={Input}
            placeholder="Enter weight"
            valueIsNumericString={true}
            decimalScale={2}
            allowNegative={false}
            name="weight"
          />
          <div className="flex flex-row gap-2 items-center min-w-[100px] justify-end">
            <span>
              {additionalTonnage > 0
                ? safeMoneyFromCents(additionalTonnageCost).toFormat()
                : '$0.00'}
            </span>
          </div>
        </div>
      </div>

      {/* Container Days Section */}
      <div className="flex flex-col gap-2 w-full text-sm text-muted">
        <label className="text-sm text-muted">
          Container Service Days - {bundleDetails.includedDays} days included
        </label>
        {containers.map((c) => {
          const overage = containerOverages.find((co) => co.containerId === c._id);
          return (
            <div key={c._id} className="flex flex-col gap-2">
              <div className="flex flex-row w-full items-center gap-4">
                <label className="whitespace-nowrap">
                  {c.friendlyId} ({c.containerTypeInfo.name})
                </label>
                <NumericFormat
                  thousandSeparator={true}
                  type="text"
                  inputMode="numeric"
                  value={state.usageByContainer[c._id] || 0}
                  onValueChange={(values) => {
                    updateState({
                      usageByContainer: {
                        ...state.usageByContainer,
                        [c._id]: parseInt(values.value) || 0,
                      },
                    });
                  }}
                  className="w-full"
                  min={0}
                  customInput={Input}
                  placeholder="0"
                  valueIsNumericString={true}
                  decimalScale={0}
                  name={c._id}
                  step={1}
                />

                <span className="min-w-[100px] text-right">
                  {overage && overage.refundDays > 0
                    ? `- ${safeMoneyFromCents(overage.refundAmount).toFormat()}`
                    : overage && overage.additionalDays > 0
                      ? safeMoneyFromCents(overage.additionalCost).toFormat()
                      : '$0.00'}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      <div className="h-[1px] w-full bg-disabled"></div>

      {/* Additional Materials Section */}
      <h4 className="font-semibold text-base">Additional Materials</h4>
      <div className="flex flex-col gap-2.5 max-h-96 overflow-y-auto">
        {!materials && <div className="text-sm text-muted">Loading materials...</div>}
        {materials && materials.length === 0 && (
          <div className="text-sm text-muted">No materials available</div>
        )}
        {materials && materials.filter((m) => m.extraChargeInCents > 0).length === 0 && (
          <div className="text-sm text-muted">No materials with additional charges</div>
        )}
        {materials
          ?.sort((a, b) => a.name.localeCompare(b.name))
          ?.filter((m) => m.extraChargeInCents > 0)
          .map((material) => (
            <div
              key={material._id}
              className="flex flex-row gap-2 w-full items-center text-muted font-medium text-base"
            >
              <span className="flex-1">{material.name}</span>
              <div className="flex-1 flex flex-row gap-4 items-center">
                <Counter
                  onChange={(e) =>
                    updateState({
                      additionalMaterials: {
                        ...state.additionalMaterials,
                        [material._id]: e,
                      },
                    })
                  }
                  value={state.additionalMaterials[material._id] || 0}
                />
              </div>
              <p className="min-w-16">
                {MoneyFromCents(
                  (state.additionalMaterials[material._id] || 0) * material.extraChargeInCents,
                ).toFormat()}
              </p>
            </div>
          ))}
      </div>
    </div>
  );
};
